import React, { useEffect, useMemo, useState } from 'react';
import { Cheque } from '@baemingo/table-events/dist/types';
import styled from '@emotion/styled';
import TableItem from '../../Components/TableItem';
import { useSelector } from '~Hooks';
import { DateTime } from 'luxon';
import Marker from '~Components/Marker';
import Structure from '~Components/Structure';
import { makeIsTableVisible } from '~Utils/visibleTables';

const Wrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  min-height: 500px;
  height: 100%;
  width: 100%;
  position: relative;
  overflow: auto;
`;

const InnerWrapper = styled.div<{
  posY: number;
  posX: number;
}>`
  position: absolute;
  top: ${(props) => props.posY}px;
  left: ${(props) => props.posX}px;
`;

interface Props {
  selectedFloorPlan: string;
  allCheques: Cheque[];
  selectedTable: string | undefined;
  selectedTableIds?: string[]; //Used for selected tables in table select modal (bookings move to table)
  setSelectedTable: (id: string, name: string, coverage?: number) => void;
  onTableDoubleClick?: (id: string, name: string, coverage?: number) => void;
}

const Floorplan: React.FunctionComponent<Props> = ({
  selectedFloorPlan,
  allCheques,
  selectedTable,
  selectedTableIds,
  setSelectedTable,
  onTableDoubleClick,
}) => {
  const companyTimezone = useSelector((state) => state.companyTimeZone);
  const floorPlans = useSelector((state) => state.floorPlans);

  // Include deleted tables that still have a cheque, so the cheque can be reached
  const isTableVisible = makeIsTableVisible(allCheques);
  const allTables = floorPlans
    .flatMap((fp) => fp.tables)
    .filter((table) => table.floorPlan.id === selectedFloorPlan && isTableVisible(table));

  const markers = floorPlans
    .find((fp) => fp.id === selectedFloorPlan)
    ?.markers.map((marker) => marker);

  const structures = floorPlans
    .find((fp) => fp.id === selectedFloorPlan)
    ?.structures.map((structure) => structure);

  const [timeCounter, setTimeCounter] = useState(0);
  const [tableActivityMap, setTableActivityMap] = useState<
    Record<string, { activeAt: string | false; createdAt: string | false }>
  >({});

  useEffect(() => {
    const intervalId = setInterval(() => setTimeCounter((counter) => counter + 1), 60000);
    return () => clearInterval(intervalId);
  }, [selectedTable]);

  useEffect(() => {
    const newActivityMap: Record<
      string,
      { activeAt: string | false; createdAt: string | false }
    > = {};

    allTables?.forEach((table) => {
      const tableCheques = allCheques.filter((item) => item.tableId === table.id);
      if (tableCheques.length > 0) {
        // Get the first cheque's ID for this table (or whichever is relevant)
        const cheque = tableCheques[0];
        const activeAt = cheque.activeAt || false;
        const createdAt = cheque.createdAt || false;
        if (activeAt && createdAt) {
          newActivityMap[table.id] = { activeAt, createdAt };
        }
      }
    });

    setTableActivityMap(newActivityMap);
  }, [timeCounter, selectedTable]);

  const { staleTables, moderateTables } = useMemo(() => {
    const now = DateTime.now().setZone(companyTimezone);
    const tableActivityDiffs: Array<{ tableId: string; diffMinutes: number }> = [];

    // Calculate time differences for each table
    Object.entries(tableActivityMap).forEach(([tableId, data]) => {
      const { activeAt, createdAt } = data;

      if (activeAt && createdAt) {
        const activeTime = DateTime.fromISO(activeAt);
        const createdTime = DateTime.fromISO(createdAt);

        // Ensure we have valid dates
        if (!activeTime.isValid || !createdTime.isValid) return;

        // Calculate time differences
        const activeTimeDiff = now.diff(activeTime, 'minutes').minutes;
        const createdTimeDiff = now.diff(createdTime, 'minutes').minutes;

        // Only include tables with:
        // 1. Cheques created within last 4 hours
        // 2. Activity between 15 minutes and 2 hours ago
        if (createdTimeDiff <= 240 && activeTimeDiff >= 15 && activeTimeDiff <= 120) {
          tableActivityDiffs.push({
            tableId,
            diffMinutes: activeTimeDiff,
          });
        }
      }
    });

    // Sort tables by activity difference (oldest first)
    tableActivityDiffs.sort((a, b) => b.diffMinutes - a.diffMinutes);

    // Calculate how many tables to include in each category, include at least 1 table or round up
    const totalFilteredTables = tableActivityDiffs.length;
    const staleCount = Math.max(1, Math.round(totalFilteredTables * 0.05));
    const moderateCount = Math.max(1, Math.round(totalFilteredTables * 0.05));

    // Get the tables for each category
    const staleTableIds = tableActivityDiffs.slice(0, staleCount).map((item) => item.tableId);
    const moderateTableIds = tableActivityDiffs
      .slice(staleCount, staleCount + moderateCount)
      .map((item) => item.tableId);

    return {
      staleTables: staleTableIds,
      moderateTables: moderateTableIds,
    };
  }, [tableActivityMap, companyTimezone]);

  // Determine activity status for a table
  const getActiveStatus = (tableId: string) => {
    if (staleTables.includes(tableId)) return 'stale';
    if (moderateTables.includes(tableId)) return 'moderate';
    return 'none';
  };

  return (
    <Wrapper>
      {allTables?.map((table) => {
        const tableCheques = allCheques.filter((item) => item.tableId === table.id);
        const activityStatus = getActiveStatus(table.id);

        const nrOfPeople =
          tableCheques.length > 0
            ? Math.max(...tableCheques.map((item) => (item.coverage ? item.coverage : 0)))
            : undefined;

        return (
          <InnerWrapper key={table.id} posX={table.posX} posY={table.posY}>
            <TableItem
              id={table.id}
              selected={
                selectedTableIds ? selectedTableIds.includes(table.id) : table.id === selectedTable
              }
              active={tableCheques.length > 0}
              activityStatus={activityStatus}
              hasPrinted={tableCheques.some((c) => c.printed)}
              courseStageId={tableCheques.find((c) => c.stage)?.stage ?? null}
              tableName={table.code}
              tableShape={table.rotation}
              peopleCount={nrOfPeople}
              setSelectedTable={(id, name) => setSelectedTable(id, name, nrOfPeople)}
              onDoubleClick={
                onTableDoubleClick
                  ? (id, name) => onTableDoubleClick(id, name, nrOfPeople)
                  : undefined
              }
            />
          </InnerWrapper>
        );
      })}

      {markers?.map((marker) => {
        return (
          <InnerWrapper key={marker.id} posX={marker.posX} posY={marker.posY}>
            <Marker icon={marker.icon} size={marker.size} />
          </InnerWrapper>
        );
      })}

      {structures?.map((structure) => {
        return (
          <Structure
            key={structure.id}
            posX={structure.posX}
            posY={structure.posY}
            width={structure.width}
            height={structure.height}
            rotation={structure.rotation || 0}
          />
        );
      })}
    </Wrapper>
  );
};

export default Floorplan;
